import type { InventoryUnitRecord } from '@/renderer/types/inventory';
import { Button, DropdownSelect, Modal } from '@components/common';
import { FormInput } from '@components/common/FormField';
import React, { useEffect, useState } from 'react';
import {
  createEmptyInventoryUnitForm,
  createInventoryUnitFormFromRecord,
  InventoryUnitFormState,
} from './inventorySettingsForm';

interface InventoryUnitEditorModalProps {
  unit: InventoryUnitRecord | null;
  isSaving: boolean;
  onClose: () => void;
  onSave: (form: InventoryUnitFormState) => Promise<void>;
}

export function InventoryUnitEditorModal({
  unit,
  isSaving,
  onClose,
  onSave,
}: InventoryUnitEditorModalProps) {
  const [form, setForm] = useState<InventoryUnitFormState>(
    createEmptyInventoryUnitForm(),
  );

  useEffect(() => {
    setForm(
      unit
        ? createInventoryUnitFormFromRecord(unit)
        : createEmptyInventoryUnitForm(),
    );
  }, [unit]);

  const update = <K extends keyof InventoryUnitFormState>(
    key: K,
    value: InventoryUnitFormState[K],
  ) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={unit ? 'Edit inventory unit' : 'Create inventory unit'}
      size="lg"
    >
      <div className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <FormInput
            label="Unit name"
            value={form.name}
            onChange={(event) => update('name', event.target.value)}
            placeholder="Example: Bottle"
            required
          />
          <FormInput
            label="Abbreviation"
            value={form.abbreviation}
            onChange={(event) => update('abbreviation', event.target.value)}
            placeholder="Example: btl"
          />
        </div>

        <DropdownSelect
          label="Status"
          value={form.isActive ? 'active' : 'inactive'}
          options={[
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
          ]}
          onChange={(value) => update('isActive', value === 'active')}
        />

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              onSave(form);
            }}
            disabled={!form.name.trim()}
            isLoading={isSaving}
          >
            {unit ? 'Save changes' : 'Create unit'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
